import { useCallback, useEffect, useRef, useState } from "react";
import type { AdminDossierStateResponse, AdminDossierVersion, Locale } from "../../shared/dto";
import { translateCount } from "../i18n";
import { clientPickerLabel, clientPickerOptions } from "./client-picker";
import { formatDate } from "./core";
import { staffMutation, type StaffMatrixClient } from "./model";
import "./admin-settings-dossier.css";

/**
 * Servis wave 5: dossier import becomes a validated wizard.
 *
 * The import protocol (reserve, batches, finalize) is hash checked end to end,
 * which is right for a runner and hostile to a human: the old console showed
 * three sentences about what the runner does and no way to do it. The wizard
 * keeps the SAME protocol and moves the hashing into the browser: paste the
 * facts, get every problem named with its position before anything is sent,
 * see what will be created, then watch the three steps run.
 *
 * Nothing is destroyed on failure. A half finished import stays as an
 * "importing" version, the live dossier keeps serving, and repeating the
 * wizard creates a fresh version.
 */

export const DOSSIER_CATEGORIES = ["identity", "business", "audience", "offer", "voice", "proof", "other"] as const;
export const DOSSIER_BATCH_SIZE = 50;

export interface DossierFactDraft {
  factKey: string;
  category: string;
  text: string;
  source: unknown;
  confidence?: number | null;
  supersedesFactId?: string | null;
}

export interface DossierIssue {
  key: string;
  position: number | null;
  value?: string;
}

export interface DossierValidation {
  ok: boolean;
  facts: DossierFactDraft[];
  issues: DossierIssue[];
  categories: Array<{ category: string; count: number }>;
}

/** The worker hashes a stable JSON rendering (sorted keys, dropped undefined).
 * The browser must produce the SAME string or every batch is rejected, so this
 * mirrors worker/src/profile.ts stableJson exactly. */
export function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${stableJson(item)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Every rule the server enforces, checked here first and reported with the
 * position in the file. Normalisation matches the server parser field for
 * field: an accepted payload hashes to the same value on both sides.
 */
export function validateDossierPayload(raw: string): DossierValidation {
  const empty: DossierValidation = { ok: false, facts: [], issues: [], categories: [] };
  const text = raw.trim();
  if (!text) return { ...empty, issues: [{ key: "emptyInput", position: null }] };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ...empty, issues: [{ key: "notJson", position: null }] };
  }
  const list = Array.isArray(parsed)
    ? parsed
    : (parsed && typeof parsed === "object" && Array.isArray((parsed as { facts?: unknown }).facts))
      ? (parsed as { facts: unknown[] }).facts
      : null;
  if (!list) return { ...empty, issues: [{ key: "notFactList", position: null }] };
  if (list.length === 0) return { ...empty, issues: [{ key: "noFacts", position: null }] };

  const issues: DossierIssue[] = [];
  const facts: DossierFactDraft[] = [];
  const seen = new Set<string>();
  list.forEach((item, index) => {
    const position = index + 1;
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      issues.push({ key: "notObject", position });
      return;
    }
    const row = item as Record<string, unknown>;
    const factKey = typeof row.factKey === "string" ? row.factKey.trim() : "";
    const category = typeof row.category === "string" ? row.category : "";
    const factText = typeof row.text === "string" ? row.text : "";
    if (!factKey) issues.push({ key: "missingFactKey", position });
    else if (seen.has(factKey)) issues.push({ key: "duplicateFactKey", position, value: factKey });
    if (!DOSSIER_CATEGORIES.includes(category as (typeof DOSSIER_CATEGORIES)[number])) {
      issues.push({ key: "badCategory", position, value: category || factKey });
    }
    if (!factText.trim()) issues.push({ key: "missingText", position });
    const confidencePresent = typeof row.confidence === "number" || row.confidence === null;
    if (row.confidence !== undefined && !confidencePresent) issues.push({ key: "badConfidence", position });
    if (typeof row.confidence === "number" && (row.confidence < 0 || row.confidence > 1)) {
      issues.push({ key: "confidenceRange", position });
    }
    const supersedesPresent = typeof row.supersedesFactId === "string" || row.supersedesFactId === null;
    if (row.supersedesFactId !== undefined && !supersedesPresent) issues.push({ key: "badSupersedes", position });
    if (factKey) seen.add(factKey);
    const fact: DossierFactDraft = { factKey, category, text: factText, source: row.source ?? {} };
    if (confidencePresent) fact.confidence = row.confidence as number | null;
    if (supersedesPresent) fact.supersedesFactId = row.supersedesFactId as string | null;
    facts.push(fact);
  });

  const counts = new Map<string, number>();
  for (const fact of facts) counts.set(fact.category, (counts.get(fact.category) ?? 0) + 1);
  return {
    ok: issues.length === 0,
    facts,
    issues: issues.slice(0, 20),
    categories: [...counts.entries()].map(([category, count]) => ({ category, count }))
  };
}

export interface DossierPlan {
  contentSha256: string;
  factCount: number;
  batches: Array<{ index: number; sha256: string; factCount: number; facts: DossierFactDraft[] }>;
}

export async function buildDossierPlan(facts: DossierFactDraft[], batchSize = DOSSIER_BATCH_SIZE): Promise<DossierPlan> {
  const batches: DossierPlan["batches"] = [];
  for (let index = 0; index * batchSize < facts.length; index += 1) {
    const slice = facts.slice(index * batchSize, (index + 1) * batchSize);
    batches.push({ index, sha256: await sha256Hex(stableJson(slice)), factCount: slice.length, facts: slice });
  }
  return {
    contentSha256: await sha256Hex(stableJson({ facts })),
    factCount: facts.length,
    batches
  };
}

const ERROR_KEYS: Record<string, string> = {
  invalid_request: "invalidRequest",
  not_found: "notFound",
  forbidden: "forbidden",
  unauthorized: "forbidden",
  state_conflict: "stateConflict",
  idempotency_mismatch: "idempotency",
  import_batch_mismatch: "batchMismatch",
  import_incomplete: "incomplete",
  // S2-01: the code `staffMutation` returns when the request never left the
  // browser. Without it a dropped connection read as "neznámá chyba" and the
  // reader looked for the fault in their data.
  network_unreachable: "offline"
};

export function dossierErrorKey(code: string | null): string {
  return `staffConsole.dossierWizard.error.${(code && ERROR_KEYS[code]) || "unknown"}`;
}

type RunStep = { key: "reserve" | "upload" | "finalize"; state: "waiting" | "running" | "done" | "failed"; detail?: string };

type RunState =
  | { kind: "idle" }
  | { kind: "running"; steps: RunStep[] }
  | { kind: "done"; steps: RunStep[]; version: number; factCount: number }
  | { kind: "failed"; steps: RunStep[]; errorKey: string };

export function AdminDossierImport(props: {
  clients: StaffMatrixClient[];
  csrfToken: string;
  locale: Locale;
  t: (key: string) => string;
}) {
  const { t } = props;
  const [clientId, setClientId] = useState("");
  const [raw, setRaw] = useState("");
  const [validation, setValidation] = useState<DossierValidation | null>(null);
  const [plan, setPlan] = useState<DossierPlan | null>(null);
  const [versions, setVersions] = useState<AdminDossierVersion[] | null>(null);
  const [run, setRun] = useState<RunState>({ kind: "idle" });
  // The picker is a real button over a hidden input (the same idiom as the
  // materials upload and the F61 profile photo), not a <label> wearing button
  // classes: a label gets none of the button box model, so it rendered as a
  // 22px text line beside a 48px action (audit S-13), and it answered no Enter.
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [fileError, setFileError] = useState(false);

  const loadVersions = useCallback(async (id: string) => {
    if (!id) { setVersions(null); return; }
    try {
      const response = await fetch(`/api/admin/diagnostics/dossier/${encodeURIComponent(id)}`, { credentials: "include" });
      if (!response.ok) throw new Error("dossier_state_failed");
      const body = await response.json() as AdminDossierStateResponse;
      setVersions(body.versions);
    } catch {
      setVersions(null);
    }
  }, []);

  useEffect(() => { void loadVersions(clientId); }, [clientId, loadVersions]);

  async function check() {
    const result = validateDossierPayload(raw);
    setValidation(result);
    setPlan(result.ok ? await buildDossierPlan(result.facts) : null);
    setRun({ kind: "idle" });
  }

  async function importDossier() {
    if (!clientId || !plan) return;
    const steps: RunStep[] = [
      { key: "reserve", state: "running" },
      { key: "upload", state: "waiting" },
      { key: "finalize", state: "waiting" }
    ];
    setRun({ kind: "running", steps: [...steps] });
    const path = `/api/admin/clients/${encodeURIComponent(clientId)}/dossiers`;
    const fail = (index: number, code: string | null) => {
      steps[index] = { ...steps[index], state: "failed" };
      setRun({ kind: "failed", steps: [...steps], errorKey: dossierErrorKey(code) });
    };
    const errorCode = async (response: Response): Promise<string | null> => {
      const body = await response.json().catch(() => ({})) as { error?: string };
      return body.error ?? null;
    };

    const reserve = await staffMutation(path, {
      method: "POST", csrfToken: props.csrfToken,
      body: {
        operation: "reserve",
        source: "admin_console",
        expectedContentSha256: plan.contentSha256,
        expectedFactCount: plan.factCount,
        manifest: { batches: plan.batches.map(({ index, sha256, factCount }) => ({ index, sha256, factCount })) }
      }
    });
    if (!reserve.ok) { fail(0, await errorCode(reserve)); return; }
    const { version } = await reserve.json() as { version: number };
    steps[0] = { key: "reserve", state: "done" };

    for (const batch of plan.batches) {
      steps[1] = {
        key: "upload", state: "running",
        detail: t("staffConsole.dossierWizard.batchProgress")
          .replace("{index}", String(batch.index + 1))
          .replace("{total}", String(plan.batches.length))
      };
      setRun({ kind: "running", steps: [...steps] });
      const response = await staffMutation(path, {
        method: "POST", csrfToken: props.csrfToken,
        body: { operation: "batch", version, batchIndex: batch.index, batchSha256: batch.sha256, facts: batch.facts }
      });
      if (!response.ok) { fail(1, await errorCode(response)); return; }
    }
    steps[1] = { key: "upload", state: "done" };
    steps[2] = { key: "finalize", state: "running" };
    setRun({ kind: "running", steps: [...steps] });

    const finalize = await staffMutation(path, {
      method: "POST", csrfToken: props.csrfToken,
      body: { operation: "finalize", version, contentSha256: plan.contentSha256 }
    });
    if (!finalize.ok) { fail(2, await errorCode(finalize)); return; }
    const result = await finalize.json() as { version: number; factCount: number };
    steps[2] = { key: "finalize", state: "done" };
    setRun({ kind: "done", steps: [...steps], version: result.version, factCount: result.factCount });
    await loadVersions(clientId);
  }

  const liveVersion = versions?.find((item) => item.status === "ready") ?? null;

  return (
    // S2-12: the name and the sentence are the panel's header (AdminPanel);
    // repeating them thirty pixels lower is a second title, not a description.
    <section className="dossier-wizard" aria-label={t("staffConsole.dossierWizard.title")}>

      <label className="inline-select">
        <span>{t("staffConsole.client")}</span>
        <select value={clientId} onChange={(event) => { setClientId(event.target.value); setRun({ kind: "idle" }); }}>
          <option value="">{t("staffConsole.team.choose")}</option>
          {clientPickerOptions(props.clients).map((option) => (
            <option key={option.id} value={option.id}>{clientPickerLabel(option, t)}</option>
          ))}
        </select>
      </label>

      {clientId && (
        <p className="dossier-current" aria-live="polite">
          {liveVersion
            ? t("staffConsole.dossierWizard.currentVersion")
              .replace("{version}", String(liveVersion.version))
              .replace("{count}", String(liveVersion.receivedFactCount))
              .replace("{date}", liveVersion.finalizedAt ? formatDate(liveVersion.finalizedAt, props.locale) : "")
            : t("staffConsole.dossierWizard.noVersion")}
        </p>
      )}

      <label className="field-label" htmlFor="dossier-payload">{t("staffConsole.dossierWizard.payloadLabel")}</label>
      <textarea id="dossier-payload" className="dossier-payload" rows={8} value={raw}
        onChange={(event) => { setRaw(event.target.value); setValidation(null); setPlan(null); setRun({ kind: "idle" }); }} />
      <p className="admin-tool-hint">{t("staffConsole.dossierWizard.payloadHint")}</p>
      <div className="button-row dossier-actions">
        <button className="secondary-button dossier-file" type="button" onClick={() => fileRef.current?.click()}>
          {t("staffConsole.dossierWizard.pickFile")}
        </button>
        <input ref={fileRef} type="file" hidden accept="application/json,.json" onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared right away, so picking the SAME file after an edit fires
          // change again instead of looking like a dead button.
          event.target.value = "";
          if (!file) return;
          setFileError(false);
          // A file the browser cannot read used to leave the button silent,
          // which reads as "it does nothing". Every failure names itself.
          void file.text()
            .then((content) => { setRaw(content); setValidation(null); setPlan(null); setRun({ kind: "idle" }); })
            .catch(() => setFileError(true));
        }} />
        <button className="secondary-button" type="button" disabled={!raw.trim()} onClick={() => void check()}>
          {t("staffConsole.dossierWizard.check")}
        </button>
      </div>
      {/* S2-19: a closed control names its condition next to itself; a `title`
          never reaches the reader, because a disabled element takes no
          pointer. */}
      {!raw.trim() && <p className="admin-tool-hint">{t("staffConsole.dossierWizard.checkNeedsFacts")}</p>}
      {fileError && (
        <p className="message error-message" role="alert">{t("staffConsole.dossierWizard.fileUnreadable")}</p>
      )}

      {validation && <DossierValidationReport validation={validation} plan={plan} t={t} />}

      {validation?.ok && plan && (
        <div className="dossier-plan">
          {/* 🔴 S3-11: the last sentence an administrator reads before creating
              a new version of a client's dossier said "Nahraje se 1 faktů v 1
              dávkách" for every count. Czech declines, and the project wrote
              that rule down (P-12); the family this needed already existed a
              few keys away (`feedbackCount`). Two counts, two families, because
              five facts can travel in one batch. */}
          <p>
            {t("staffConsole.dossierWizard.plan")
              .replace("{version}", String((versions?.[0]?.version ?? 0) + 1))
              .replace("{facts}", translateCount(props.locale, "staffConsole.dossierWizard.planFacts", plan.factCount))
              .replace("{batches}", translateCount(props.locale, "staffConsole.dossierWizard.planBatches", plan.batches.length))}
          </p>
          <button className="primary-button" type="button" disabled={!clientId || run.kind === "running"}
            onClick={() => void importDossier()}>
            {t("staffConsole.dossierWizard.import")}
          </button>
          {!clientId && <p className="admin-tool-hint">{t("staffConsole.dossierWizard.pickClientFirst")}</p>}
        </div>
      )}

      <DossierRunReport run={run} clientId={clientId} t={t} />
    </section>
  );
}

export function DossierValidationReport(props: {
  validation: DossierValidation;
  plan: DossierPlan | null;
  t: (key: string) => string;
}) {
  const { validation, t } = props;
  if (!validation.ok) {
    return (
      <div className="message error-message dossier-issues" role="alert">
        <strong>{t("staffConsole.dossierWizard.invalid").replace("{count}", String(validation.issues.length))}</strong>
        <ul>
          {validation.issues.map((issue, index) => (
            <li key={`${issue.key}-${issue.position ?? index}`}>
              {t(`staffConsole.dossierWizard.issue.${issue.key}`)
                .replace("{position}", String(issue.position ?? 0))
                .replace("{value}", issue.value ?? "")
                .replace("{categories}", DOSSIER_CATEGORIES.join(", "))}
            </li>
          ))}
        </ul>
        <span>{t("staffConsole.dossierWizard.invalidNext")}</span>
      </div>
    );
  }
  return (
    <div className="message success-message dossier-summary" role="status">
      <strong>{t("staffConsole.dossierWizard.valid").replace("{count}", String(validation.facts.length))}</strong>
      <ul className="dossier-categories">
        {validation.categories.map((row) => (
          <li key={row.category}>
            {`${t(`staffConsole.dossierWizard.category.${row.category}`)}: ${row.count}`}
          </li>
        ))}
      </ul>
      <ul className="dossier-preview">
        {validation.facts.slice(0, 3).map((fact) => (
          <li key={fact.factKey}>{`${fact.factKey}: ${fact.text.slice(0, 120)}`}</li>
        ))}
      </ul>
    </div>
  );
}

export function DossierRunReport(props: { run: RunState; clientId: string; t: (key: string) => string }) {
  const { run, t } = props;
  if (run.kind === "idle") return null;
  return (
    <div className="dossier-run" aria-live="polite">
      <ol className="dossier-steps">
        {run.steps.map((step) => (
          <li key={step.key} className={`dossier-step state-${step.state}`} data-dossier-step={step.key}>
            <span>{t(`staffConsole.dossierWizard.step.${step.key}`)}</span>
            <span className="dossier-step-state">{t(`staffConsole.dossierWizard.stepState.${step.state}`)}</span>
            {step.detail && <span className="dossier-step-detail">{step.detail}</span>}
          </li>
        ))}
      </ol>
      {run.kind === "done" && (
        <div className="message success-message" role="status">
          <strong>
            {t("staffConsole.dossierWizard.doneTitle")
              .replace("{version}", String(run.version))
              .replace("{count}", String(run.factCount))}
          </strong>
          <span>{t("staffConsole.dossierWizard.doneNext")}</span>
          {props.clientId && (
            <a className="text-button compact-button" href={`/?client=${encodeURIComponent(props.clientId)}`}>
              {t("staffConsole.activity.openClient")}
            </a>
          )}
        </div>
      )}
      {run.kind === "failed" && (
        <div className="message error-message" role="alert">
          <strong>{t(run.errorKey)}</strong>
          <span>{t("staffConsole.dossierWizard.failedSafe")}</span>
        </div>
      )}
    </div>
  );
}
